import Box from '@mui/material/Box';
import Button from '@mui/material/Button';
import CircularProgress from '@mui/material/CircularProgress';
import Dialog from '@mui/material/Dialog';
import DialogActions from '@mui/material/DialogActions';
import DialogContent from '@mui/material/DialogContent';
import DialogTitle from '@mui/material/DialogTitle';
import Typography from '@mui/material/Typography';
import { DatePicker, TimePicker, LocalizationProvider } from '@mui/x-date-pickers';
import { AdapterDayjs } from '@mui/x-date-pickers/AdapterDayjs';
import dayjs from 'dayjs';
import React, { useCallback, useEffect, useState } from 'react';
import 'dayjs/locale/en-au';

import { ACCENT_SX, BulkRunProps } from './dialogShared';
import { BulkRunStatus } from '../../../components/molecules/BulkRunStatus';
import { formatLocalDateTime } from '../../../utils/common';

export const ScheduleDialog: React.FC<
  {
    open: boolean;
    isBulk: boolean;
    count: number;
    submitting: boolean;
    lastError: string | null;
    onClose: () => void;
    onSubmit: (date: string) => Promise<void>;
  } & BulkRunProps
> = ({ open, isBulk, count, submitting, lastError, onClose, onSubmit, progress, failures }) => {
  const [draftDate, setDraftDate] = useState<dayjs.Dayjs | null>(null);
  const [draftTime, setDraftTime] = useState<dayjs.Dayjs | null>(null);
  const [validationError, setValidationError] = useState('');

  useEffect(() => {
    if (open) {
      setDraftDate(null);
      setDraftTime(dayjs().hour(9).minute(0));
      setValidationError('');
    }
  }, [open]);

  const handleSubmit = useCallback(() => {
    if (!draftDate || !draftDate.isValid()) {
      setValidationError('Please enter a valid date');
      return;
    }
    if (!draftTime || !draftTime.isValid()) {
      setValidationError('Please enter a valid time');
      return;
    }
    const combined = draftDate.hour(draftTime.hour()).minute(draftTime.minute()).second(0);
    onSubmit(formatLocalDateTime(combined));
  }, [draftDate, draftTime, onSubmit]);

  const title = isBulk ? `Schedule Job (${count} jobs)` : 'Schedule Job';
  const isValid = draftDate?.isValid() && draftTime?.isValid();

  return (
    <Dialog
      open={open}
      onClose={submitting ? undefined : onClose}
      maxWidth="xs"
      fullWidth
      aria-label={title}
    >
      <DialogTitle>{title}</DialogTitle>
      <DialogContent>
        <LocalizationProvider dateAdapter={AdapterDayjs} adapterLocale="en-au">
          <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2, mt: 1 }}>
            <DatePicker
              label="Date"
              value={draftDate}
              onChange={(val) => {
                setDraftDate(val);
                setValidationError('');
              }}
              format="DD/MM/YYYY"
              slotProps={{ textField: { size: 'small', fullWidth: true } }}
            />
            <TimePicker
              label="Time"
              value={draftTime}
              onChange={(val) => {
                setDraftTime(val);
                setValidationError('');
              }}
              ampm={true}
              slotProps={{ textField: { size: 'small', fullWidth: true } }}
            />
          </Box>
        </LocalizationProvider>
        {validationError && (
          <Typography sx={{ color: 'error.main', fontSize: '12px', mt: 1 }}>
            {validationError}
          </Typography>
        )}
        <BulkRunStatus
          progress={progress}
          submitting={submitting}
          failures={failures}
          error={lastError}
          verb="Scheduling"
        />
      </DialogContent>
      <DialogActions>
        <Button onClick={onClose} disabled={submitting} aria-label="Cancel schedule">
          Cancel
        </Button>
        <Button
          variant="contained"
          onClick={handleSubmit}
          disabled={!isValid || submitting}
          sx={ACCENT_SX}
          aria-label="Submit schedule"
        >
          {submitting ? <CircularProgress size={18} color="inherit" /> : 'Schedule'}
        </Button>
      </DialogActions>
    </Dialog>
  );
};
